import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { WINDOW } from '@shared/shared/tokens/window';
import { ACTIVITIES_FORM_KEY } from 'app.config';
import * as moment from 'moment';
import { filter, map, of, switchMap, take, tap } from 'rxjs';
import { GET_ACTIVITIES_SEARCH_RESULTS, GetActivitiesSearchResults, RestoreCarSearch, RestoreHotels } from '../actions';
import {
  INITIALIZE_DATE,
  INITIALIZE_TRAVEL_SEARCH_FORM,
  InitializeDate,
  InitializeTravelSearchForm,
  RESTORE_ACTIVITIES_FORM,
  RestoreActivitiesForm,
  SetDefaultStartDate,
  UpdateActivitiesForm,
} from '../actions/travel-search-form.actions';

@Injectable()
export class TravelSearchFormEffects {
  private siteSettings$ = this.siteSettingsService.fetchSiteSettings();
  private advanceWindowDays: number;
  private defaultStartDate: string;
  private defaultEndDate: string;

  constructor(
    private actions$: Actions,
    @Inject(WINDOW) private window: Window,
    private siteSettingsService: SiteSettingsService,
    private store: Store<any>
  ) {
    this.siteSettings$
      .pipe(
        filter((siteSettings: ISiteSettings) => this.siteSettingsService.settingsRetrieved && !!siteSettings.id),
        take(1)
      )
      .subscribe((settings) => {
        this.advanceWindowDays = +settings.advance_booking_window;
        this.defaultStartDate = moment()
          .add(this.advanceWindowDays, 'd')
          .hours(12)
          .minutes(0)
          .seconds(0)
          .milliseconds(0)
          .toString();
        this.defaultEndDate = moment()
          .add(this.advanceWindowDays + 1, 'd')
          .hours(12)
          .minutes(0)
          .seconds(0)
          .milliseconds(0)
          .toString();
        return this.store.dispatch(new InitializeTravelSearchForm());
      });
  }

  public initializeTravelSearchForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INITIALIZE_TRAVEL_SEARCH_FORM),
      switchMap(() =>
        of(new InitializeDate(), new RestoreActivitiesForm(), new RestoreCarSearch(), new RestoreHotels())
      )
    )
  );

  public initStartDateDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INITIALIZE_DATE),
      map(() => new SetDefaultStartDate({ startDate: this.defaultStartDate, endDate: this.defaultEndDate }))
    )
  );

  public storeActivitiesForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GET_ACTIVITIES_SEARCH_RESULTS),
        tap((action: GetActivitiesSearchResults) => {
          this.window.localStorage.setItem(ACTIVITIES_FORM_KEY, JSON.stringify(action.form));
        })
      ),
    { dispatch: false }
  );

  public restoreActivitiesForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RESTORE_ACTIVITIES_FORM),
      map(() => JSON.parse(this.window.localStorage.getItem(ACTIVITIES_FORM_KEY))),
      filter((rawForm) => !!rawForm),
      map((rawForm) => {
        const storedStartDate = new Date(rawForm.start_date_full);
        const defaultStartDate = new Date(this.defaultStartDate);
        const currentDate = new Date();
        const storedEndDate = new Date(rawForm.end_date_full);
        const defaultEndDate = new Date(this.defaultEndDate);

        const form = {
          valid: true,
          data: {
            ...rawForm,
            start_date_full: storedStartDate <= currentDate ? defaultStartDate : storedStartDate,
            end_date_full: storedEndDate <= currentDate ? defaultEndDate : storedEndDate,
          },
        };
        return new UpdateActivitiesForm(form);
      })
    )
  );
}
